import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';

import classnames from 'classnames/bind';
import {
    FileText,
    Paperclip,
    Eye,
    Edit3,
    Shield,
    Image,
    CheckCircle2,
} from 'lucide-react';

import useDocumentNameModal from '@HOOKS/store/modals/useDocumentNameModal';
import usePreviewDocumentModal from '@HOOKS/store/modals/usePreviewDocumentModal';
import useDeleteDocumentModal from '@HOOKS/store/modals/useDeleteDocumentModal';
import usePublishDocumentModal from '@HOOKS/store/modals/usePublishDocumentModal';
import useDocumentPermissions from '@HOOKS/useDocumentPermissions';

import { AdminSingleDocument, useUpdateDocument, attachmentsAdminApi, useGetAllUsers, useUpdateDocumentPermissions } from '@SERVICES';
import { BASE_URL } from '@CONSTANTS/API.constant';

import ActionButton from '@COMPONENTS/COMMON/buttons/ActionButton/actionButton';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import PublishAllButton from '@COMPONENTS/COMMON/buttons/PublishAllButton';
import { Avatar } from '@mui/material';
import HeaderImage from '@COMPONENTS/HEADERS/HeaderImage';
import { notify } from '@NOTIFICATION/Notificator';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import useAuth from '@HOOKS/useAuth';
import useAttachmentManagementModal from '@HOOKS/store/modals/useAttachmentManagementModal';
import PermissionModal from '../PermissionModal';
import styles from './DocumentName.module.scss';
import DocumentTags from '../DocumentTags';

const cx: CX = classnames.bind(styles);

// Define PermissionItem type locally
type PermissionItem = {
    id: number;
    name: string;
    email: string;
    can_generate_document: boolean;
    can_edit_document: boolean;
    can_delete_document: boolean;
    can_publish_document: boolean;
    can_manage_document_tags: boolean;
    can_manage_document_vendors: boolean;
};

function DocumentName(props: Props) {
    const { documentData } = props;
    const { state: { user, permissions } } = useAuth();

    const { canEditDocument, canDeleteDocument, canGenerateDocument, canPublishDocument } = useDocumentPermissions({
        documentOwnerId: documentData.data.owner.id,
        permissions: documentData.meta.permissions,
    });

    const [
        updateDocument,
        {
            isSuccess, isError, error,
        },
    ] = useUpdateDocument();

    const { actions } = useDocumentNameModal(() => true);

    const { actions: previewDocumentModalActions } = usePreviewDocumentModal(
        () => true,
    );

    const { actions: deleteDocumentModalActions } = useDeleteDocumentModal(() => true);

    const { actions: publishDocumentModalActions } = usePublishDocumentModal(() => true);

    const { actions: attachmentManagementModalActions } = useAttachmentManagementModal(() => true);

    // Get attachment count for badge
    const { data: attachments = [] } = attachmentsAdminApi.useGetAttachmentsQuery({
        content_type: 'document',
        object_id: documentData.data.id,
    });

    // Check if document is fully published
    const isPublished = useMemo(() => {
        const { chapters } = documentData.data;
        return chapters.every((chapter) => {
            const { introductionText, sections } = chapter;

            return (
                introductionText.isPublished
                && sections.every((section) => {
                    const { baseText, variants } = section;

                    return (
                        variants.every((variant) => variant.isPublished)
                        && baseText.isPublished
                    );
                })
            );
        });
    }, [documentData.data]);

    const locked = !canEditDocument;

    // Cover image handling
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [showImagePreview, setShowImagePreview] = useState(false);

    // Permissions modal handling
    const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false);
    const [selectedPermissions, setSelectedPermissions] = useState<PermissionItem[]>([]);

    const [
        updateDocumentPermissions,
        {
            isLoading: isPermissionLoading,
            isSuccess: isPermissionSuccess,
            isError: isPermissionError,
        },
    ] = useUpdateDocumentPermissions();

    const {
        data: usersData = { data: [] }, isLoading: isUsersLoading, isError: isUsersError,
    } = useGetAllUsers();

    const getAvatarName = (fullName: string) => (fullName.trim()
        ? fullName
            .trim()
            .split(/\s+/)
            .map((word) => word[0].toUpperCase())
            .join('')
        : '');

    // Construct full image URL from the path - following same pattern as MainHeader
    const getDocumentCoverImageUrl = (imagePath: string | null | undefined): string | undefined => {
        if (!imagePath) return undefined;
        
        // If it's already a full URL, return as is
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }
        
        // If it's a relative path, construct the full URL
        return `${BASE_URL}${imagePath}`;
    };

    const onUpload = (imageBase: any) => {
        updateDocument({
            id: documentData.data.id,
            name: documentData.data.name,
            cover: imageBase || null,
        });
    };

    const handleImageCardClick = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    const handleImageUpload = useCallback((file: File | null) => {
        if (file) {
            updateDocument({
                id: documentData.data.id,
                name: documentData.data.name,
                cover: file,
            });
        }
    }, [documentData, updateDocument]);

    const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleImageUpload(file);
        }
    }, [handleImageUpload]);

    const handleImagePreview = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setShowImagePreview(true);
    }, []);

    const handleRemoveImage = useCallback(() => {
        updateDocument({
            id: documentData.data.id,
            name: documentData.data.name,
            cover: null,
        });
    }, [documentData, updateDocument]);

    const handleDeleteDocument = useCallback(() => {
        deleteDocumentModalActions.open({
            id: documentData.data.id,
            name: documentData.data.name,
        });
    }, [deleteDocumentModalActions, documentData.data.id, documentData.data.name]);

    // Permissions modal logic
    useEffect(() => {
        if (documentData.data.users) {
            // Convert existing users to PermissionItem format
            const convertedPermissions = documentData.data.users.map((existingUser) => ({
                id: existingUser.id,
                name: existingUser.fullName,
                email: existingUser.email,
                can_generate_document: existingUser.permissions.some((p) => p.codename === 'can_generate_document'),
                can_edit_document: existingUser.permissions.some((p) => p.codename === 'can_edit_document'),
                can_delete_document: existingUser.permissions.some((p) => p.codename === 'can_delete_document'),
                can_publish_document: existingUser.permissions.some((p) => p.codename === 'can_publish_document'),
                can_manage_document_tags: existingUser.permissions.some((p) => p.codename === 'can_manage_document_tags'),
                can_manage_document_vendors: existingUser.permissions.some((p) => p.codename === 'can_manage_document_vendors'),
            }));
            setSelectedPermissions(convertedPermissions);
        }
    }, [documentData]);

    const handlePermissionsCardClick = useCallback(() => {
        setIsPermissionsModalOpen(true);
    }, []);

    const handlePermissionsModalClose = useCallback(() => {
        setIsPermissionsModalOpen(false);
    }, []);

    const addUser = useCallback((newUser: any) => {
        const newPermission: PermissionItem = {
            id: newUser.id,
            name: newUser.fullName || newUser.email,
            email: newUser.email,
            can_generate_document: false,
            can_edit_document: false,
            can_delete_document: false,
            can_publish_document: false,
            can_manage_document_tags: false,
            can_manage_document_vendors: false,
        };
        setSelectedPermissions(prev => [...prev, newPermission]);
    }, []);

    const removeUser = useCallback((userId: number) => {
        setSelectedPermissions(prev => prev.filter(permissionUser => permissionUser.id !== userId));
    }, []);

    const handleCheckboxChange = useCallback((userId: number, permission: string, checked: boolean) => {
        setSelectedPermissions(prev => prev.map(permissionUser => {
            if (permissionUser.id === userId) {
                return { ...permissionUser, [permission]: checked };
            }
            return permissionUser;
        }));
    }, []);

    const savePermissions = useCallback(() => {
        // Convert PermissionItem[] to the expected format for the API
        const usersPermissions = selectedPermissions
            .map((permissionUser: PermissionItem) => {
                const userPermissions: any[] = [];
                if (permissionUser.can_generate_document) userPermissions.push('can_generate_document');
                if (permissionUser.can_edit_document) userPermissions.push('can_edit_document');
                if (permissionUser.can_delete_document) userPermissions.push('can_delete_document');
                if (permissionUser.can_publish_document) userPermissions.push('can_publish_document');
                if (permissionUser.can_manage_document_tags) userPermissions.push('can_manage_document_tags');
                if (permissionUser.can_manage_document_vendors) userPermissions.push('can_manage_document_vendors');

                return {
                    userId: permissionUser.id,
                    permissions: userPermissions,
                };
            })
            .filter((permissionUser: any) => permissionUser.permissions.length > 0);

        updateDocumentPermissions({
            id: documentData.data.id,
            usersPermissions,
        });
    }, [selectedPermissions, documentData.data.id, updateDocumentPermissions]);

    // Create options for permission modal
    const permissionOptions = usersData.data
        .filter((availableUser) => (
            !selectedPermissions.find((u: PermissionItem) => u.id === availableUser.id)
            && documentData.data.owner.id !== availableUser.id
        ))
        .map((availableUser) => ({
            ...availableUser,
            value: availableUser.id,
            label: availableUser.fullName || availableUser.email,
        }));

    useEffect(() => {
        if (isSuccess) {
            notify.info('Document has been updated', {
                toastId: 'update-document',
            });
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError) {
            if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'update-document-permission-error',
                });
            } else {
                notify.error('Something wrong', {
                    toastId: 'update-document-error',
                });
            }
        }
    }, [isError, error]);

    useEffect(() => {
        if (isPermissionSuccess) {
            notify.success('Document permissions updated successfully', {
                toastId: 'update-document-permissions-success',
            });
            setIsPermissionsModalOpen(false);
        }
    }, [isPermissionSuccess]);

    useEffect(() => {
        if (isPermissionError) {
            notify.error('Failed to update document permissions', {
                toastId: 'update-document-permissions-error',
            });
        }
    }, [isPermissionError]);

    const canManagePermissions = user!.id === documentData.data.owner.id || permissions.isSuperuser;

    return (
        <div className={cx('page-header')}>
            <div className={cx('page-wrap')}>
                {/* Main header section with document name and primary info */}
                <div className={cx('header-main')}>
                    <div className={cx('title-section')}>
                        <div className={cx('title-content')}>
                            <div className={cx('name')}>
                                {`Document - ${documentData.data.name}`}
                            </div>
                            <div className={cx('document-owner')}>
                                <span className={cx('owner-label')}>by</span>
                                <div className={cx('owner-info')}>
                                    <Avatar className={cx('owner-avatar')}>
                                        {getAvatarName(documentData.data.owner.fullName)}
                                    </Avatar>
                                    <span className={cx('owner-name')}>
                                        {documentData.data.owner.fullName || documentData.data.owner.email}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className={cx('title-actions')}>
                            <ActionButton
                                icon="preview"
                                buttonText="Preview"
                                tooltipTitle="Document preview"
                                variant="outlined"
                                handleClick={() => {
                                    previewDocumentModalActions.open({
                                        documentData: documentData.data,
                                        canGenerateDocument,
                                    });
                                }}
                            />
                            {canDeleteDocument && (
                                <div className={cx('delete-basic-button')}>
                                    <BasicButton
                                        title="Delete"
                                        variant="danger"
                                        size="small"
                                        icon="trash"
                                        onClick={handleDeleteDocument}
                                        tooltip="Delete document"
                                    />
                                </div>
                            )}
                            {!locked && (
                                <BasicButton
                                    title="Rename"
                                    variant="neutral"
                                    size="small"
                                    icon="edit"
                                    onClick={() => {
                                        actions.open({
                                            documentData: documentData.data,
                                        });
                                    }}
                                    tooltip="Edit document name"
                                />
                            )}
                            {!isPublished && (
                                <div className={cx('publish-wrapper')}>
                                    <PublishAllButton
                                        disabled={isPublished}
                                        canPublishDocument={canPublishDocument}
                                        onClick={() => {
                                            if (!isPublished) {
                                                publishDocumentModalActions.open({
                                                    documentData: documentData.data,
                                                });
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Options section with clickable cards */}
                <div className={cx('options-section')}>
                    <div className={cx('options-header')}>
                        <h3 className={cx('options-title')}>Document Options</h3>
                        <div className={cx('options-divider')} />
                    </div>
                    
                    <div className={cx('options-grid')}>
                        {/* Upload Cover Image Option Card */}
                        {canEditDocument && (
                            <div 
                                className={cx('option-card', { 'has-image': !!documentData.data.cover })}
                                onClick={handleImageCardClick}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleImageCardClick();
                                    }
                                }}
                            >
                                <div className={cx('option-content')}>
                                    <div className={cx('option-icon')}>
                                        <Image size={20} />
                                    </div>
                                    <div className={cx('option-info')}>
                                        <div className={cx('option-title')}>
                                            {documentData.data.cover ? 'Update Document Cover Image' : 'Upload Document Cover Image'}
                                        </div>
                                        <div className={cx('option-description')}>
                                            {documentData.data.cover ? 'Click to change the cover image for this document' : 'Click to set a cover image for this document'}
                                        </div>
                                    </div>
                                    <div className={cx('option-actions')}>
                                        {documentData.data.cover && (
                                            <button
                                                className={cx('preview-button')}
                                                onClick={handleImagePreview}
                                                type="button"
                                                title="View current image"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        )}
                                        <div className={cx('option-status')}>
                                            {documentData.data.cover && <CheckCircle2 size={18} />}
                                        </div>
                                    </div>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileInputChange}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        )}

                        {/* Manage Attachments Option Card */}
                        <div 
                            className={cx('option-card')}
                            onClick={() => {
                                attachmentManagementModalActions.open({
                                    parentType: 'document',
                                    parentId: documentData.data.id,
                                    parentName: documentData.data.name,
                                    canEdit: canEditDocument,
                                });
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    attachmentManagementModalActions.open({
                                        parentType: 'document',
                                        parentId: documentData.data.id,
                                        parentName: documentData.data.name,
                                        canEdit: canEditDocument,
                                    });
                                }
                            }}
                        >
                            <div className={cx('option-content')}>
                                <div className={cx('option-icon')}>
                                    <Paperclip size={20} />
                                </div>
                                <div className={cx('option-info')}>
                                    <div className={cx('option-title')}>
                                        Manage Attachments
                                        {attachments.length > 0 && (
                                            <span className={cx('attachment-count')}>
                                                {' '}({attachments.length})
                                            </span>
                                        )}
                                    </div>
                                    <div className={cx('option-description')}>
                                        Upload, organize, and manage PDF attachments for this document
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Manage Permissions Option Card */}
                        {canManagePermissions && (
                            <div 
                                className={cx('option-card', 'permissions-card')}
                                onClick={handlePermissionsCardClick}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handlePermissionsCardClick();
                                    }
                                }}
                            >
                                <div className={cx('option-content')}>
                                    <div className={cx('option-icon')}>
                                        <Shield size={20} />
                                    </div>
                                    <div className={cx('option-info')}>
                                        <div className={cx('option-title')}>
                                            Manage Permissions
                                        </div>
                                        <div className={cx('option-description')}>
                                            Control who can view, edit, and manage this document
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Image Preview Modal */}
            {showImagePreview && documentData.data.cover && (
                <div 
                    className={cx('image-preview-modal')} 
                    onClick={() => setShowImagePreview(false)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            setShowImagePreview(false);
                        }
                    }}
                >
                    <div 
                        className={cx('image-preview-content')} 
                        onClick={(e) => e.stopPropagation()}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                            }
                        }}
                    >
                        <div className={cx('image-preview-header')}>
                            <h3>Document Cover Image</h3>
                            <button
                                className={cx('close-preview')}
                                onClick={() => setShowImagePreview(false)}
                                type="button"
                            >
                                ×
                            </button>
                        </div>
                        <div className={cx('image-preview-body')}>
                            <img 
                                src={getDocumentCoverImageUrl(documentData.data.cover)} 
                                alt="Document cover" 
                                onError={(e) => {
                                    console.log('❌ Document cover image failed to load:', getDocumentCoverImageUrl(documentData.data.cover));
                                    e.currentTarget.style.display = 'none';
                                }}
                                onLoad={() => {
                                    console.log('✅ Document cover image loaded successfully:', getDocumentCoverImageUrl(documentData.data.cover));
                                }}
                            />
                        </div>
                        <div className={cx('image-preview-actions')}>
                            <BasicButton
                                title="Change Image"
                                variant="primary"
                                size="small"
                                onClick={() => {
                                    setShowImagePreview(false);
                                    handleImageCardClick();
                                }}
                            />
                            <BasicButton
                                title="Remove Image"
                                variant="danger"
                                size="small"
                                onClick={() => {
                                    setShowImagePreview(false);
                                    handleRemoveImage();
                                }}
                            />
                            <BasicButton
                                title="Close"
                                variant="neutral"
                                size="small"
                                onClick={() => setShowImagePreview(false)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Permissions Modal */}
            {isPermissionsModalOpen && (
                <PermissionModal
                    handleClose={handlePermissionsModalClose}
                    options={permissionOptions}
                    usersData={selectedPermissions}
                    addUser={addUser}
                    handleCheckboxChange={handleCheckboxChange}
                    removeUser={removeUser}
                    savePermissions={savePermissions}
                />
            )}
        </div>
    );
}

type Props = {
    documentData: AdminSingleDocument;
};

export default DocumentName;
